import { Button, Empty, Select, Space, Spin, Table, Typography } from "antd";
import Link from "antd/es/typography/Link";
import {
  addMinutes,
  differenceInMinutes,
  isBefore,
  isWeekend,
  setHours,
  startOfDay,
} from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { api } from "../../../config";
import { AuthenticationState } from "../../../interfaces/users";
import SuggestionEventDrawer, {
  SuggestionDraftEvent,
} from "../SuggestionEventDrawer";
import AgendaTimeline from "../../Shared/Components/AssemblersAgenda/AgendaTimeline";
import { getAssemblyTimelineEventClassName } from "../../../utils/helpers/assemblyTimelineEventHelpers";

const { Text } = Typography;

const BUFFER_MINUTES = 60;

interface SuggestScheduleProps {
  onSchedule: () => void;
}

interface SuggestionZone {
  id: number;
  name: string;
  assemblersCount: number;
}

interface SuggestionGroup {
  id: number;
  title: string;
  seniorityId: number;
}

interface AvailableTicket {
  ticketId: number;
  title: string;
  city: string;
  neighborhood: string;
}

interface SuggestionEventApi {
  id: number;
  kind: "draft" | "canonical";
  start: string | Date;
  end: string | Date;
  description: string;
  fieldReportId: number;
  assemblerId: number;
  assembler?: {
    id: number;
    fullName: string;
    seniorityId: number;
  };
  fieldReport?: any;
  durationMinutes?: number;
  seniorityId?: number;
}

interface SuggestionPreviewResponse {
  groups: SuggestionGroup[];
  canonicalEvents: SuggestionEventApi[];
  draftEvents: SuggestionEventApi[];
  availableTickets: AvailableTicket[];
}

interface TimelineItem extends SuggestionEventApi {
  start: Date;
  end: Date;
  start_time: Date;
  end_time: Date;
  title: string;
  group: number;
  className: string;
  canMove: boolean;
  canResize: boolean;
  canChangeGroup: boolean;
}

const getInitialTimeRange = () => ({
  start: setHours(startOfDay(new Date()), 6).getTime(),
  end: setHours(startOfDay(new Date()), 22).getTime(),
});

const mapEventToTimelineItem = (event: SuggestionEventApi): TimelineItem => {
  const start = new Date(event.start);
  const end = new Date(event.end);
  const seniorityId =
    event.seniorityId ??
    event.fieldReport?.seniorityId ??
    event.assembler?.seniorityId ??
    3;

  return {
    ...event,
    start,
    end,
    start_time: start,
    end_time: end,
    title: event.description,
    group: event.assemblerId,
    seniorityId,
    className: `${event.kind === "draft" ? "draft-event" : "canonical-event"} ${getAssemblyTimelineEventClassName(event)}`,
    canMove: event.kind === "draft",
    canResize: event.kind === "draft",
    canChangeGroup: event.kind === "draft",
  };
};

const hasSameTimeRange = (
  left: { start: number; end: number },
  right: { start: number; end: number }
) => left.start === right.start && left.end === right.end;

const SuggestSchedule = ({ onSchedule }: SuggestScheduleProps) => {
  const { token } = useSelector((state: AuthenticationState) => state);
  const [selectedZoneId, setSelectedZoneId] = useState<number>();
  const [groups, setGroups] = useState<SuggestionGroup[]>([]);
  const [canonicalEvents, setCanonicalEvents] = useState<TimelineItem[]>([]);
  const [draftEvents, setDraftEvents] = useState<TimelineItem[]>([]);
  const [availableTickets, setAvailableTickets] = useState<AvailableTicket[]>(
    []
  );
  const [timeRange, setTimeRange] = useState(getInitialTimeRange());
  const [view, setView] = useState<"day" | "week">("day");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedDraftEvent, setSelectedDraftEvent] =
    useState<SuggestionDraftEvent | null>(null);

  const { data: zones = [] } = useQuery<SuggestionZone[]>(
    "assembly-suggestion-zones",
    () => api(token).get("assembly-suggestions/zones")
  );

  useEffect(() => {
    if (!selectedZoneId && zones.length) {
      setSelectedZoneId(zones[0].id);
    }
  }, [zones, selectedZoneId]);

  const loadPreview = async (includeDrafts: boolean) => {
    if (!selectedZoneId) {
      setGroups([]);
      setCanonicalEvents([]);
      setDraftEvents([]);
      setAvailableTickets([]);
      return;
    }

    try {
      setLoadingPreview(true);

      const response = await api(token).post(
        "assembly-suggestions/preview",
        {
          routingZoneId: selectedZoneId,
          start: new Date(timeRange.start),
          end: new Date(timeRange.end),
          view,
          includeDrafts,
        }
      );

      const preview = response as SuggestionPreviewResponse;
      setGroups(preview.groups ?? []);
      setCanonicalEvents(
        (preview.canonicalEvents ?? []).map(mapEventToTimelineItem)
      );
      setDraftEvents((preview.draftEvents ?? []).map(mapEventToTimelineItem));
      setAvailableTickets(preview.availableTickets ?? []);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ??
          "Não foi possível carregar a prévia de agendamento"
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    setDraftEvents([]);
    setSelectedDraftEvent(null);
    loadPreview(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedZoneId, timeRange.start, timeRange.end, view]);

  const allTimelineEvents = useMemo(
    () => [...canonicalEvents, ...draftEvents],
    [canonicalEvents, draftEvents]
  );

  const pendingAvailableTickets = useMemo(() => {
    const suggestedTicketIds = new Set(
      draftEvents.map((event) => event.fieldReportId)
    );

    return availableTickets.filter(
      (ticket) => !suggestedTicketIds.has(ticket.ticketId)
    );
  }, [availableTickets, draftEvents]);

  const handleTimeRangeChange = (nextTimeRange: {
    start: number;
    end: number;
  }) => {
    setTimeRange((currentTimeRange) =>
      hasSameTimeRange(currentTimeRange, nextTimeRange)
        ? currentTimeRange
        : nextTimeRange
    );
  };

  const validateDraftEvent = (
    draftEvent: TimelineItem,
    nextStart: Date,
    nextEnd: Date,
    nextAssemblerId: number
  ) => {
    if (isBefore(nextStart, new Date())) {
      return "Não é possível mover a sugestão para uma data passada";
    }

    if (nextEnd <= nextStart) {
      return "A duração da sugestão precisa ser maior que zero";
    }

    if (isWeekend(nextStart) || isWeekend(nextEnd)) {
      return "A distribuição não permite sábado ou domingo";
    }

    const workStart = setHours(startOfDay(nextStart), 6);
    const workEnd = setHours(startOfDay(nextStart), 22);

    if (nextStart < workStart || nextEnd > workEnd) {
      return "A sugestão precisa respeitar a jornada entre 06:00 e 22:00";
    }

    const visibleStart = new Date(timeRange.start);
    const visibleEnd = new Date(timeRange.end);

    if (nextStart < visibleStart || nextEnd > visibleEnd) {
      return "A sugestão precisa permanecer dentro do intervalo visível";
    }

    const assembler = groups.find((group) => group.id === nextAssemblerId);
    const ticketSeniority = draftEvent.fieldReport?.seniorityId ?? 3;

    if (!assembler || assembler.seniorityId > ticketSeniority) {
      return "O montador selecionado possui senioridade inferior à exigida pelo ticket";
    }

    const hasConflict = [...canonicalEvents, ...draftEvents]
      .filter((event) => event.id !== draftEvent.id)
      .filter((event) => event.assemblerId === nextAssemblerId)
      .some((event) => {
        const blockedStart = addMinutes(event.start, -BUFFER_MINUTES);
        const blockedEnd = addMinutes(event.end, BUFFER_MINUTES);
        return nextStart < blockedEnd && nextEnd > blockedStart;
      });

    if (hasConflict) {
      return "O horário sugerido conflita com outro evento ou com o intervalo mínimo de 60 minutos";
    }

    return null;
  };

  const updateDraftEvent = (draftId: number, updater: (draft: TimelineItem) => TimelineItem) => {
    setDraftEvents((currentDrafts) =>
      currentDrafts.map((draft) => (draft.id === draftId ? updater(draft) : draft))
    );
  };

  const handleMoveDraft = (
    eventId: number | string,
    newStartTime: number,
    newAssemblerId: number
  ) => {
    const draftId = Number(eventId);
    const draftEvent = draftEvents.find((event) => event.id === draftId);

    if (!draftEvent) {
      return;
    }

    const nextStart = new Date(newStartTime);
    const durationMinutes = differenceInMinutes(draftEvent.end, draftEvent.start);
    const nextEnd = addMinutes(nextStart, durationMinutes);
    const validationError = validateDraftEvent(
      draftEvent,
      nextStart,
      nextEnd,
      newAssemblerId
    );

    if (validationError) {
      toast.error(validationError);
      return;
    }

    const assembler = groups.find((group) => group.id === newAssemblerId);

    updateDraftEvent(draftId, (event) => ({
      ...event,
      assemblerId: newAssemblerId,
      assembler: assembler
        ? {
            id: assembler.id,
            fullName: assembler.title,
            seniorityId: assembler.seniorityId,
          }
        : event.assembler,
      start: nextStart,
      end: nextEnd,
      start_time: nextStart,
      end_time: nextEnd,
      group: newAssemblerId,
    }));
  };

  const handleResizeDraft = (eventId: number | string, newEndTime: number) => {
    const draftId = Number(eventId);
    const draftEvent = draftEvents.find((event) => event.id === draftId);

    if (!draftEvent) {
      return;
    }

    const nextEnd = new Date(newEndTime);
    const validationError = validateDraftEvent(
      draftEvent,
      draftEvent.start,
      nextEnd,
      draftEvent.assemblerId
    );

    if (validationError) {
      toast.error(validationError);
      return;
    }

    updateDraftEvent(draftId, (event) => ({
      ...event,
      end: nextEnd,
      end_time: nextEnd,
    }));
  };

  const removeDraft = (draftEvent: SuggestionDraftEvent) => {
    setDraftEvents((currentDrafts) =>
      currentDrafts.filter((event) => event.id !== draftEvent.id)
    );
    setSelectedDraftEvent(null);
  };

  const saveDraft = async (
    draftEvent: SuggestionDraftEvent,
    nextEnd: Date
  ) => {
    const currentDraft = draftEvents.find((event) => event.id === draftEvent.id);
    if (!currentDraft || !selectedZoneId) {
      return;
    }

    const validationError = validateDraftEvent(
      currentDraft,
      currentDraft.start,
      nextEnd,
      currentDraft.assemblerId
    );

    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      const response = await api(token).post("assembly-suggestions/confirm", {
        routingZoneId: selectedZoneId,
        fieldReportId: currentDraft.fieldReportId,
        assemblerId: currentDraft.assemblerId,
        start: currentDraft.start,
        end: nextEnd,
        visibleStart: new Date(timeRange.start),
        visibleEnd: new Date(timeRange.end),
      });

      const canonicalEvent = mapEventToTimelineItem(
        response as SuggestionEventApi
      );

      setDraftEvents((currentDrafts) =>
        currentDrafts.filter((event) => event.id !== draftEvent.id)
      );
      setCanonicalEvents((currentEvents) =>
        [...currentEvents, canonicalEvent].sort(
          (left, right) => left.start.getTime() - right.start.getTime()
        )
      );
      setAvailableTickets((currentTickets) =>
        currentTickets.filter(
          (ticket) => ticket.ticketId !== currentDraft.fieldReportId
        )
      );
      setSelectedDraftEvent(null);
      toast.success("Evento salvo com sucesso");
      onSchedule();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ??
          "Não foi possível salvar a sugestão selecionada"
      );
    }
  };

  const selectedDraft = selectedDraftEvent
    ? (draftEvents.find((event) => event.id === selectedDraftEvent.id) as
        | TimelineItem
        | undefined)
    : undefined;
  const selectedDraftDrawerEvent = selectedDraft
    ? ({
        id: selectedDraft.id,
        kind: "draft",
        start: selectedDraft.start,
        end: selectedDraft.end,
        fieldReportId: selectedDraft.fieldReportId,
        assemblerId: selectedDraft.assemblerId,
        assembler: selectedDraft.assembler,
      } as SuggestionDraftEvent)
    : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <Space wrap>
          <div className="flex min-w-[280px] flex-col gap-1">
            <Text strong>Região</Text>
            <Select
              value={selectedZoneId}
              placeholder="Selecione a região"
              onChange={(value) => setSelectedZoneId(value)}
              options={zones.map((zone) => ({
                value: zone.id,
                label: `${zone.name} (${zone.assemblersCount} montadores)`,
              }))}
            />
          </div>
          <Button
            type="primary"
            disabled={!selectedZoneId}
            loading={loadingPreview}
            onClick={() => loadPreview(true)}
          >
            Distribuir Tickets
          </Button>
          <Button
            disabled={!draftEvents.length}
            onClick={() => {
              setDraftEvents([]);
              setSelectedDraftEvent(null);
            }}
          >
            Limpar Sugestões
          </Button>
        </Space>

        <div className="flex gap-4 text-sm">
          <Text>Canônicos: {canonicalEvents.length}</Text>
          <Text>Sugestões: {draftEvents.length}</Text>
          <Text>Disponíveis: {pendingAvailableTickets.length}</Text>
        </div>
      </div>

      {!selectedZoneId ? (
        <Empty description="Selecione uma região para visualizar a agenda de distribuição" />
      ) : (
        <>
          <div className="relative">
            {loadingPreview ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                <Spin size="large" />
              </div>
            ) : null}

            <AgendaTimeline
              events={allTimelineEvents}
              groups={groups}
              defaultAtualView="day"
              defaultTimeRange={timeRange}
              onEventMove={handleMoveDraft}
              onEventResize={handleResizeDraft}
              onEventDoubleClick={(eventId) => {
                const draft = draftEvents.find(
                  (event) => event.id === Number(eventId)
                );
                if (!draft) {
                  return;
                }

                setSelectedDraftEvent({
                  id: draft.id,
                  kind: "draft",
                  start: draft.start,
                  end: draft.end,
                  fieldReportId: draft.fieldReportId,
                  assemblerId: draft.assemblerId,
                  assembler: draft.assembler,
                });
              }}
              onSetTimeRange={handleTimeRangeChange}
              onSetAtualView={(nextView) =>
                setView((currentView) =>
                  currentView === nextView
                    ? currentView
                    : (nextView as "day" | "week")
                )
              }
              enableItemMove
              enableItemResize
            />
          </div>

          <div className="flex flex-col gap-2">
            <Text strong>Tickets disponíveis para distribuição</Text>
            <Table
              size="small"
              rowKey="ticketId"
              dataSource={pendingAvailableTickets}
              pagination={false}
              locale={{
                emptyText: "Nenhum ticket disponível para distribuição",
              }}
            >
              <Table.Column
                title="Ticket"
                dataIndex="title"
                key="title"
                render={(title: string, ticket: AvailableTicket) => (
                  <Link
                    href={`/montagem?id=${ticket.ticketId}`}
                    target="_blank"
                  >
                    {title}
                  </Link>
                )}
              />
              <Table.Column title="Cidade" dataIndex="city" key="city" />
              <Table.Column
                title="Bairro"
                dataIndex="neighborhood"
                key="neighborhood"
              />
            </Table>
          </div>
        </>
      )}

      <SuggestionEventDrawer
        open={Boolean(selectedDraft)}
        draftEvent={selectedDraftDrawerEvent}
        onClose={() => setSelectedDraftEvent(null)}
        onRemove={removeDraft}
        onSave={saveDraft}
      />
    </div>
  );
};

export default SuggestSchedule;
